// react
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// theme
import { theme } from '@/theme';

// components
import { Button, Card, Dialog, Icon } from '@/components/atoms';
import { Icons } from '@/components/atoms/Icon';

// models
import { ActionPlanItem } from '@karehero/models';

export const ActionPlanCard = ({
  id,
  icon,
  title,
  color = 'blue',
  subTitle,
  imgSrc,
  to,
  onDismiss,
}: ActionPlanCardProps) => {
  // state
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);

  return (
    <>
      <ContentItem to={to} color={color}>
        <StyledCard elevation={3}>
          <CardHeader $color={color} $imgSrc={imgSrc}>
            {icon && <Icon icon={icon} fontSize={24} />}
          </CardHeader>
          <CardBody>
            <BodyTitle>{title}</BodyTitle>
            <BodyText>{subTitle}</BodyText>
          </CardBody>
        </StyledCard>
      </ContentItem>
      <Dialog
        isOpen={isDismissDialogOpen}
        setIsOpen={setIsDismissDialogOpen}
        isCloseButton={false}
        title='Are you sure you want to dismiss this action?'
        description='Dismissed actions can still be viewed in the all actions section.'
      >
        <ActionButtonsWrapper>
          <ActionButtons>
            <Button
              ariaLabel='dismiss action'
              variant='primary'
              onPress={() => {
                onDismiss?.(id);
                setIsDismissDialogOpen(false);
              }}
            >
              Dismiss action
            </Button>
            <Button
              ariaLabel='cancel'
              variant='secondary'
              onPress={() => setIsDismissDialogOpen(false)}
            >
              Cancel
            </Button>
          </ActionButtons>
        </ActionButtonsWrapper>
      </Dialog>
    </>
  );
};

const ContentItem = styled(Link)<ColourProps>`
  display: block;
  color: ${(props) => props.theme.color.actionPlanCards[props.color]?.primary};
  cursor: pointer;
  user-select: none;
  text-decoration: none;
  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.03);
  }
`;

const StyledCard = styled(Card)`
  gap: 0;
  border: 0;
  padding: 0;
  height: 100%;
  display: flex;
  margin: ${(props) => props.theme.spacing[2]};
  background: none;
`;

interface CardHeaderProps {
  $color: string;
  $imgSrc?: string;
}
const CardHeader = styled.div<CardHeaderProps>`
  position: relative;
  width: 17%;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px 0 0 8px;
  background: ${(props) =>
    props.theme.color.actionPlanCards[props.$color]?.secondary};
  color: ${(props) => props.theme.color.actionPlanCards[props.$color]?.primary};
  margin-right: ${(props) => props.theme.spacing[16]};
  overflow: hidden;
  ${(props) =>
    props.$imgSrc &&
    css`
      color: ${(props) => props.theme.color.neutral[10]};
      background-image: url(${props.$imgSrc});
      background-size: cover;
      background-position: top;
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.2);
        z-index: 1;
      }
      & > * {
        z-index: 2;
      }
    `}
`;

const CardBody = styled.div`
  width: 70%;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  gap: ${(props) => props.theme.spacing[8]};
  padding: ${(props) => props.theme.spacing[12]} 0;
`;

const BodyTitle = styled.div`
  color: ${(props) => props.theme.color.neutral[100]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const BodyText = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  max-width: 300px;
  width: 100%;
`;

interface ColourProps {
  color: keyof typeof theme.color.cards;
}

export interface ActionPlanCardProps extends ActionPlanItem {
  color?: string;
  icon: Icons;
  to: string;
  imgSrc?: string;
  onDismiss?: (id: string) => void;
  isModify?: boolean;
}

ActionPlanCard.displayName = 'ActionPlanCard';

export default ActionPlanCard;
